import {
  IncentiveAward,
  IncentiveCategory,
  IncentiveLifecycleStatus,
} from '@varo/graphql-schema-types';
import backgroundTextureLight from 'assets/images/vxl/component/productIntroScreen/background-texture-light.png';
import { NativeStackHeaderProps, Stack } from 'expo-router';
import { INCENTIVE_HEADER_TOP_PADDING } from 'features/incentive/constants';
import { DEFAULT_SWEEPSTAKES_ILLUSTRATION } from 'features/incentive/content/sweepstakesContentV1';
import { getIncentiveCopyPrefix } from 'features/incentive/helpers/getIncentiveCopyPrefix';
import { getWinnerAwardDisplay } from 'features/incentive/helpers/getWinnerAwardDisplay';
import {
  getIncentiveButtonAnalytics,
  IncentiveScreenNames,
} from 'features/incentive/helpers/incentiveAnalytics';
import { useIncentiveScreenViewed } from 'features/incentive/hooks/useIncentiveAnalytics';
import useIncentiveProgram from 'features/incentive/hooks/useIncentiveProgram';
import { IncentivePrerequisiteProduct } from 'features/incentive/prerequisite/incentivePrerequisite';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { VaroPressableNames } from 'shared/analytics/types/pressableNames';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import ProductIntroScreen from 'shared/patterns/vxl/ProductIntroScreen/ProductIntroScreen';
import theme from 'shared/styles/theme';

interface IncentiveWinnerScreenProps {
  incentiveType: IncentiveCategory;
  incentiveProgramId: string;
  lifecycleStatus: IncentiveLifecycleStatus;
  awards: IncentiveAward[];
  prerequisiteProduct: IncentivePrerequisiteProduct | undefined;
}

function IncentiveWinnerScreen({
  incentiveType,
  incentiveProgramId,
  lifecycleStatus,
  awards,
  prerequisiteProduct,
}: IncentiveWinnerScreenProps) {
  const { t: translatedText } = useTranslation('incentive', {
    keyPrefix: getIncentiveCopyPrefix(incentiveType, 'winner'),
  });
  const { content } = useIncentiveProgram(incentiveType, incentiveProgramId);
  const award = getWinnerAwardDisplay(awards);
  const accountLastFour = prerequisiteProduct?.accountLastFour;

  useIncentiveScreenViewed({
    screenName: IncentiveScreenNames.WINNER,
    incentiveType,
    incentiveProgramId,
    lifecycleStatus,
  });

  const getBody = () => {
    if (!award) {
      return translatedText('bodyWithoutAward');
    }

    return accountLastFour
      ? translatedText('body', { ...award, accountLastFour })
      : translatedText('bodyWithoutAccount', { ...award });
  };

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: '',
          headerShown: true,
          headerTransparent: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={navigateBack}
              transparent
            />
          ),
        }}
      />
      <AmplitudeMask style={styles.mask}>
        <ProductIntroScreen
          visual={
            content?.content.illustration ?? DEFAULT_SWEEPSTAKES_ILLUSTRATION
          }
          headlineTextStyle="expressive"
          backgroundTexture={backgroundTextureLight}
          topPadding={INCENTIVE_HEADER_TOP_PADDING}
          eyebrowText={translatedText('eyebrow')}
          headlineText={
            award
              ? translatedText('headline', { amount: award.amount })
              : translatedText('headlineWithoutAmount')
          }
          bodyContent={
            <BodyBaseText numberOfLines={0} style={styles.bodyText}>
              {getBody()}
            </BodyBaseText>
          }
          footerItem={
            <BodySmText numberOfLines={0} style={styles.footnote}>
              {translatedText('footnote')}
            </BodySmText>
          }
          secondaryButton={
            prerequisiteProduct
              ? {
                  label: prerequisiteProduct.label,
                  onPress: prerequisiteProduct.open,
                  componentAnalytics: getIncentiveButtonAnalytics(
                    incentiveType,
                    VaroPressableNames.INCENTIVE
                      .VIEW_INCENTIVE_PREREQUISITE_PRODUCT,
                  ),
                }
              : undefined
          }
        />
      </AmplitudeMask>
    </View>
  );
}

const styles = StyleSheet.create({
  bodyText: {
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    textAlign: 'center',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  footnote: {
    color: theme.color.text.secondary,
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    textAlign: 'center',
  },
  mask: {
    flex: 1,
  },
});

export default IncentiveWinnerScreen;
